
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart } from "lucide-react";
import { Product } from "@/lib/types";
import { getCategory } from "@/lib/data/categories";
import { cn, conditionLabels, formatPrice } from "@/lib/utils";
import { useAppStore } from "@/lib/store";

export default function ProductCard({
  product,
  className,
}: {
  product: Product;
  className?: string;
}) {
  const category = getCategory(product.category);
  const user = useAppStore((s) => s.currentUser());
  const isFavourited = useAppStore((s) =>
    user ? (s.favourites[user.uid] ?? []).includes(product.id) : false
  );
  const toggleFavourite = useAppStore((s) => s.toggleFavourite);

  return (
    <motion.div
      className={cn("group relative rounded-[1.35rem] border border-cyan-100/10 bg-[#0b2354]/45 p-2.5 shadow-[0_14px_34px_rgba(0,8,32,.2)] transition-[border-color,box-shadow] hover:border-yellow-200/30 hover:shadow-[0_20px_46px_rgba(0,42,110,.28)]", className)}
      whileHover="hover"
      initial="rest"
      animate="rest"
    >
      <Link to={`/product/${product.id}`} className="block">
        <div
          className="nm-grain relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/10"
          style={{ background: product.images.length ? undefined : category?.gradient }}
        >
          {product.images.length > 0 ? (
            <>
              <motion.img
                src={product.images[0]}
                alt={product.title}
                loading="lazy"
                decoding="async"
                variants={{ rest: { scale: 1, opacity: 1 }, hover: { scale: 1.05, opacity: product.images[1] ? 0 : 1 } }}
                transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-0 w-full h-full object-cover"
              />
              {product.images[1] && (
                <motion.img
                  src={product.images[1]}
                  alt={product.title}
                  loading="lazy"
                  decoding="async"
                  variants={{ rest: { opacity: 0, scale: 1.05 }, hover: { opacity: 1, scale: 1.05 } }}
                  transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              )}
            </>
          ) : (
            <motion.div
              variants={{ rest: { scale: 1 }, hover: { scale: 1.05 } }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-0 flex items-end p-4"
            >
              <span
                className="font-display text-3xl font-semibold opacity-25"
                style={{ color: category?.accent }}
              >
                {category?.name}
              </span>
            </motion.div>
          )}

          {product.isRareFind && (
            <span className="absolute top-3 left-3 text-[10px] tracking-[0.15em] uppercase text-white/90 bg-white/10 backdrop-blur-md px-2.5 py-1 rounded-full border border-white/20">
              ✦ Rare Find
            </span>
          )}

          <motion.button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              if (!user) return;
              toggleFavourite(user.uid, product.id);
            }}
            variants={{ rest: { opacity: 0, y: -4 }, hover: { opacity: 1, y: 0 } }}
            transition={{ duration: 0.2 }}
            className={cn(
              "absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center backdrop-blur-md border border-white/20 transition-colors",
              isFavourited ? "bg-[var(--nm-pink)] text-white" : "bg-black/30 text-white md:opacity-0 md:group-hover:opacity-100 opacity-100"
            )}
            aria-label="Toggle favourite"
          >
            <Heart size={14} fill={isFavourited ? "white" : "none"} />
          </motion.button>
        </div>

        <div className="mt-3 flex flex-col gap-0.5 px-1 pb-1">
          <p className="text-sm text-white line-clamp-1">{product.title}</p>
          <div className="flex items-center justify-between">
            <span className="font-display text-lg font-black text-[var(--nm-gold)]">
              {formatPrice(product.price)}
            </span>
            <span className="text-xs text-[var(--nm-ink-muted)]">
              {conditionLabels[product.condition]}
            </span>
          </div>
          <span className="text-xs text-[var(--nm-ink-muted)]">
            {product.sellerName}
          </span>
        </div>
      </Link>
    </motion.div>
  );
}
